import { convert, DateTimeFormatter, LocalDate, nativeJs } from "@js-joda/core";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { curveMonotoneX } from "@visx/curve";
import { Group } from "@visx/group";
import { scaleLinear, scaleTime } from "@visx/scale";
import { LinePath } from "@visx/shape";
import { useMemo, useRef } from "react";
import type { SocialStatsDto } from "shared";
import { useResizeObserver } from "usehooks-ts";
import {
	getDictionaryFromData,
	sumDictionaryData,
} from "../helpers/social-data-helpers";

interface DataPoint {
	x: Date;
	y: number;
}

interface ChartProps {
	data: Partial<SocialStatsDto>;
}

const margin = { top: 20, right: 30, bottom: 50, left: 40 };
const dateFormatter = DateTimeFormatter.ofPattern("yyyy-MM-dd");

export const Chart = ({ data }: ChartProps) => {
	const containerRef = useRef<HTMLDivElement>(
		null,
	) as React.RefObject<HTMLDivElement>;

	const dataDict = useMemo(() => getDictionaryFromData(data), [data]);
	const dataSummed = useMemo(
		() =>
			sumDictionaryData(dataDict).sort((a, b) =>
				LocalDate.parse(a.date).isAfter(LocalDate.parse(b.date)) ? 1 : -1,
			),
		[dataDict],
	);

	const { width = 0, height = 0 } = useResizeObserver({
		ref: containerRef,
	});

	const allDataPoints = dataSummed?.map((d) => ({
		x: LocalDate.parse(d.date, dateFormatter),
		y: d.followers,
	}));
	const minDate = allDataPoints.reduce(
		(min, d) => (d?.x.isBefore(min) ? d.x : min),
		LocalDate.now(),
	);
	const maxDate = allDataPoints.reduce(
		(max, d) => (d?.x.isAfter(max) ? d.x : max),
		LocalDate.now(),
	);

	const xScale = scaleTime({
		domain: [convert(minDate).toDate(), convert(maxDate).toDate()],
		range: [margin.left, width - margin.right],
	});

	const yScale = scaleLinear<number>({
		domain: [0, Math.max(...allDataPoints.map((d) => d?.y ?? 0))],
		range: [height - margin.bottom, margin.top],
		nice: true,
	});

	return (
		<div ref={containerRef} style={{ width: "100%", height: "400px" }}>
			<svg width={width} height={height}>
				<title>Engagement report</title>
				<Group>
					<LinePath<DataPoint>
						data={dataSummed.map((d) => ({
							x: convert(LocalDate.parse(d.date, dateFormatter)).toDate(),
							y: d.followers ?? 0,
						}))}
						x={(d) => xScale(d.x)}
						y={(d) => yScale(d.y)}
						curve={curveMonotoneX}
						stroke="#0000FF"
						strokeWidth={2}
					/>

					<AxisBottom
						top={height - margin.bottom}
						scale={xScale}
						stroke="#333"
						tickStroke="#333"
						//tickValues={uniqueXValues.map(d => 'test')}
						tickFormat={(d) => dateFormatter.format(nativeJs(d))}
					/>

					<AxisLeft
						left={margin.left}
						scale={yScale}
						stroke="#333"
						tickStroke="#333"
						//tickValues={uniqueYValues}
						tickFormat={(d: { valueOf(): number }) =>
							Math.floor(d.valueOf()).toString()
						}
					/>
				</Group>
			</svg>
		</div>
	);
};
